/**
 * Unified Credit Control Dashboard
 * Combines the best elements from Homepage and Dashboard V3
 * Updated: 2026-01-08
 * HMR stability verified
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  HandCoins,
  ListChecks,
  ListTodo,
  MessageSquare,
  Receipt,
  RefreshCw,
  Scale,
  Target,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DataModeBadge } from '@/components/DataModeBadge';
import { DashboardFilter, type DashboardFilterOption, DashboardSelector } from '@/components/dashboard';
import { PerformanceSnapshotContent } from '@/components/dashboard/performance-snapshot-content';
import { DataConsistencyChecker } from '@/components/dev/DataConsistencyChecker';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpSupportButton } from '@/components/help-support';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCompany } from '@/contexts/CompanyContext';
import { useFilter } from '@/contexts/filter-context';
import { useAgeingSummary } from '@/hooks/useAgeingSummary';
import { useCollectionQueueSummary } from '@/hooks/useCollectionQueueSummary';
import { useCreditControllersList } from '@/hooks/useCreditControllersData';
import { useDisputesWidgetSummary } from '@/hooks/useDisputesWidgetSummary';
import { useFilteredMetrics } from '@/hooks/useFilteredMetrics';
import { useHomepageMetrics } from '@/hooks/useHomepageMetrics';
import { usePerformanceSnapshot } from '@/hooks/usePerformanceSnapshot';
import { usePromisesWidgetSummary } from '@/hooks/usePromisesWidgetSummary';
import { type TodaysProgressData, useTodaysProgress } from '@/hooks/useTodaysProgress';
import {
  type UnallocatedPaymentsSummaryParams,
  useUnallocatedPaymentsSummary,
} from '@/hooks/useUnallocatedPaymentsSummary';
import { useUserContext } from '@/hooks/useUserContext';
import { useMode } from '@/providers/ModeProvider';
import { getDefaultFilter } from '@/utils/role-based-content';

// Ageing Portfolio data is now calculated from master data based on filter

/** Extracted component to reduce cognitive complexity of the main dashboard */
function TodaysProgressBar({
  progress,
  isLoading,
  isError,
  formatCurrency,
}: Readonly<{
  progress: TodaysProgressData | undefined;
  isLoading: boolean;
  isError: boolean;
  formatCurrency: (value: number) => string;
}>) {
  if (isLoading) {
    return <span className="font-semibold text-foreground">Loading...</span>;
  }

  if (isError) {
    return <span className="text-destructive">Unable to load progress</span>;
  }

  const paymentsCount = progress?.paymentsAllocated.count ?? 0;
  const paymentsAmount = progress?.paymentsAllocated.totalAmount ?? 0;
  const promisesCount = progress?.promisesKept.count ?? 0;
  const casesCount = progress?.casesResolved.count ?? 0;

  return (
    <>
      <span className="font-semibold text-foreground">
        {paymentsCount} payments allocated ({formatCurrency(paymentsAmount)})
      </span>
      {' \u2022 '}
      <span className="font-semibold text-foreground">{promisesCount} promises kept</span>
      {' \u2022 '}
      <span className="font-semibold text-foreground">{casesCount} cases resolved</span>
    </>
  );
}

function getReceiptsNavigationPath(
  selectedUserId: string | undefined,
  selectedCreditControllerId: string | undefined,
  isCreditControllerValidationLoading: boolean,
): string | undefined {
  if (isCreditControllerValidationLoading) return undefined;
  if (selectedUserId && !selectedCreditControllerId) return '/payments/receipts';

  const searchParams = new URLSearchParams({ filter: 'unallocated' });
  if (selectedCreditControllerId) {
    searchParams.set('creditControllerId', selectedCreditControllerId);
  }
  return `/payments/receipts?${searchParams.toString()}`;
}

function useUnallocatedReceiptsCard(selectedUserId?: string) {
  const navigate = useNavigate();
  const {
    data: creditControllers,
    isLoading: isCreditControllersLoading,
    isFetching: isCreditControllersFetching,
  } = useCreditControllersList({
    enabled: selectedUserId !== undefined,
    requireFreshData: true,
  });
  const selectedCreditControllerId = creditControllers?.find((x) => x.id === selectedUserId)?.id;
  const isCreditControllerValidationLoading =
    selectedUserId !== undefined && (isCreditControllersLoading || isCreditControllersFetching);
  const summaryParams: UnallocatedPaymentsSummaryParams =
    selectedUserId && !selectedCreditControllerId
      ? { outstandingNotZero: true }
      : { allocationStatus: 'unallocated', creditControllerId: selectedCreditControllerId };
  const { data, isLoading } = useUnallocatedPaymentsSummary(summaryParams, !isCreditControllerValidationLoading);
  const navigationPath = getReceiptsNavigationPath(
    selectedUserId,
    selectedCreditControllerId,
    isCreditControllerValidationLoading,
  );
  const navigateToReceipts = useCallback(() => {
    if (navigationPath) navigate(navigationPath);
  }, [navigate, navigationPath]);

  return {
    data,
    isLoading: isLoading || isCreditControllerValidationLoading,
    navigateToReceipts,
  };
}

export function UnifiedCreditControlDashboard() {
  const navigate = useNavigate();
  const { data: userContext } = useUserContext();
  const { setCurrentFilter } = useFilter();
  const queryClient = useQueryClient();
  const { isLive } = useMode();
  const { selectedCompany } = useCompany();
  const companyCurrency = selectedCompany?.currency || 'GBP';
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>({
    id: 'everyone',
    name: 'Manager view',
    type: 'all',
  });

  // Initialise the filter once user context has loaded
  // Use internalUserId (int) rather than userId (GUID) because backend
  // endpoints (widget-summary, queue-summary, etc.) bind the query
  // parameter to int and cannot parse a GUID.
  useEffect(() => {
    if (!userContext) return;
    const internalId = userContext.internalUserId;
    const userIdStr = internalId != null ? String(internalId) : userContext.userId;
    const userName = [userContext.firstName, userContext.lastName].filter(Boolean).join(' ') || userContext.email;
    const userRole = (userContext.role?.toLowerCase() ?? 'user') as Parameters<typeof getDefaultFilter>[0];
    const defaultFilterId = getDefaultFilter(userRole, userIdStr);
    const filterOptions: DashboardFilterOption[] = [
      { id: 'everyone', name: 'Manager view', type: 'all' },
      { id: userIdStr, name: userName, type: 'user' },
    ];
    const defaultOption = filterOptions.find((opt) => opt.id === defaultFilterId) ?? filterOptions[0];
    setSelectedFilter(defaultOption);
  }, [userContext]);

  // Sync local filter state with global filter context
  // This allows DashboardSelector to react to filter changes
  useEffect(() => {
    const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
    setCurrentFilter(filterId);
    console.log('[Dashboard] Filter changed to:', filterId);
  }, [selectedFilter, setCurrentFilter]);

  // Get metrics from centralized data store (for wins and key metrics)
  const { todaysWins, keyMetrics } = useHomepageMetrics();

  // Get collection queue summary (uses live API in live mode, mock data in demo mode)
  // When a specific user is selected, pass their ID so the API filters by assignedTo
  const selectedUserId = selectedFilter.id !== 'everyone' ? selectedFilter.id : undefined;
  const { data: queueSummary, isLoading: isQueueSummaryLoading } = useCollectionQueueSummary(selectedUserId);

  // Get promises widget summary (uses live API in live mode, mock data in demo mode)
  const { data: promisesWidgetSummary, isLoading: isPromisesWidgetLoading } = usePromisesWidgetSummary(selectedUserId);

  // Get unallocated payments summary (uses live API in live mode, mock data in demo mode)
  // When a specific user is selected, pass their ID as creditControllerId so the API filters by that controller
  const {
    data: unallocatedSummary,
    isLoading: isUnallocatedSummaryLoading,
    navigateToReceipts,
  } = useUnallocatedReceiptsCard(selectedUserId);

  // Get today's progress (payments allocated, promises kept, cases resolved)
  const {
    data: todaysProgress,
    isLoading: isTodaysProgressLoading,
    isError: isTodaysProgressError,
  } = useTodaysProgress(selectedUserId);
  // Get disputes widget summary (uses live API in live mode, mock data in demo mode)
  const { data: disputesWidgetSummary, isLoading: isDisputesWidgetLoading } = useDisputesWidgetSummary(selectedUserId);

  // Get performance snapshot (uses live API in live mode, mock data in demo mode)
  const {
    data: performanceSnapshot,
    isLoading: isPerformanceSnapshotLoading,
    isError: isPerformanceSnapshotError,
  } = usePerformanceSnapshot(selectedUserId);

  // Get filtered metrics based on selected filter
  const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
  const filteredMetrics = useFilteredMetrics(filterId);

  // Get ageing summary (uses live API in live mode, mock data in demo mode)
  const {
    data: ageingSummary,
    isLoading: isAgeingSummaryLoading,
    isError: isAgeingSummaryError,
  } = useAgeingSummary(selectedUserId);

  // Filter suffix for section headers
  const filterSuffix = selectedFilter.id !== 'everyone' ? ` - ${selectedFilter.name}` : '';

  // Handle refresh - invalidate all dashboard widget queries when in live mode
  const handleRefresh = useCallback(async () => {
    if (!isLive) return;

    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection-queue-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['promises-widget-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['unallocated-payments-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['disputes-widget-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['todays-progress'] }),
        queryClient.invalidateQueries({ queryKey: ['analysis', 'aging-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['performance-snapshot'] }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isLive, queryClient]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: companyCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Map the filtered metrics to the format expected by the dashboard
  const masterPriorities = {
    collectionQueue: {
      count: filteredMetrics.todaysPriorities.casesDueToday.count,
      amount: filteredMetrics.todaysPriorities.casesDueToday.value,
    },
    unallocatedPayments: {
      count: filteredMetrics.todaysPriorities.unallocatedReceipts.count,
      amount: filteredMetrics.todaysPriorities.unallocatedReceipts.value,
    },
    promisesDueToday: {
      dueCount: filteredMetrics.todaysPriorities.promisesDueToday.count,
      brokenCount: filteredMetrics.todaysPriorities.promisesDueToday.brokenCount || 0,
      dueAmount: filteredMetrics.todaysPriorities.promisesDueToday.value,
    },
    disputes: {
      count: filteredMetrics.todaysPriorities.disputesRequiringResponse.count,
      amount: filteredMetrics.todaysPriorities.disputesRequiringResponse.value,
    },
  };

  // Calculate portfolio overview data for Performance Snapshot
  const ageing = filteredMetrics.ageingPortfolio;
  const over90Amount = ageing.over91120 + ageing.over121180;
  const over90Percentage = ageing.total > 0 ? Math.round((over90Amount / ageing.total) * 100) : 0;
  const portfolioOverview = {
    totalOutstanding: ageing.total,
    customerCount: filteredMetrics.keyMetrics.customerCount,
    over90Amount,
    over90Percentage,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header Controls - Single Row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Dashboard Toggle Buttons */}
            <DashboardSelector />

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || !isLive}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {/* Filter Dropdown */}
          <DashboardFilter selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
        </div>

        {/* Global Filter Banner */}
        {selectedFilter.id !== 'everyone' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {selectedFilter.initials || selectedFilter.name.substring(0, 2).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-900">Viewing: {selectedFilter.name}'s Portfolio</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFilter({ id: 'everyone', name: 'Manager view', type: 'all' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Dashboard Title */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Credit Control Dashboard</h1>
              <DataModeBadge />
            </div>
            <div className="flex items-center gap-2">
              <FeatureFeedbackCollector feature="Dashboard" />
              <HelpSupportButton screenId="dashboard" />
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Real-time overview of outstanding invoices, collection performance, and team activity
          </p>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>

        {/* Section 1: Today's Priorities - 4 Gateway Scorecards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal-600" />
            Today's Priorities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Collection Queue */}
            <NavigationMetricCardAlt
              title="Collection Queue"
              value={
                isQueueSummaryLoading
                  ? '...'
                  : (queueSummary?.collectionQueueCount ?? masterPriorities.collectionQueue.count).toString()
              }
              description="Accounts requiring action"
              secondaryValue={
                isQueueSummaryLoading
                  ? '...'
                  : formatCurrency(
                      queueSummary?.accountsRequiringActionTotal ?? masterPriorities.collectionQueue.amount,
                    )
              }
              icon={Target}
              variant="info"
              onClick={() => {
                const params = new URLSearchParams({ status: 'Active' });
                if (selectedUserId) params.set('assignedUser', selectedUserId);
                navigate(`/collections/queue?${params.toString()}`);
              }}
            />

            {/* Unallocated Payments */}
            <NavigationMetricCardAlt
              title="Unallocated Payments"
              value={
                isUnallocatedSummaryLoading
                  ? '...'
                  : (unallocatedSummary?.unallocatedCount ?? masterPriorities.unallocatedPayments.count).toString()
              }
              description="Receipts pending"
              secondaryValue={
                isUnallocatedSummaryLoading
                  ? '...'
                  : formatCurrency(
                      unallocatedSummary?.totalOutstandingAmount ?? masterPriorities.unallocatedPayments.amount,
                    )
              }
              icon={CreditCard}
              variant="info"
              onClick={navigateToReceipts}
            />

            {/* Promises Due Today */}
            <NavigationMetricCardAlt
              title="Promises Due Today"
              value={
                isPromisesWidgetLoading
                  ? '...'
                  : (
                      promisesWidgetSummary?.promisesDueToday.count ?? masterPriorities.promisesDueToday.dueCount
                    ).toString()
              }
              description="Broken Promises"
              secondaryValue={
                isPromisesWidgetLoading
                  ? '...'
                  : `${promisesWidgetSummary?.brokenPromises.count ?? masterPriorities.promisesDueToday.brokenCount} | ${formatCurrency(promisesWidgetSummary?.brokenPromises.totalAmount ?? masterPriorities.promisesDueToday.dueAmount)}`
              }
              icon={HandCoins}
              variant="info"
              onClick={() => {
                const params = new URLSearchParams({ filter: 'due-today' });
                if (selectedUserId) params.set('assignedUser', selectedUserId);
                navigate(`/collections/promises?${params.toString()}`);
              }}
            />

            {/* Disputes */}
            <NavigationMetricCardAlt
              title="Disputes"
              value={
                isDisputesWidgetLoading
                  ? '...'
                  : (disputesWidgetSummary?.unresolvedCount ?? masterPriorities.disputes.count).toString()
              }
              description="Unresolved Disputes"
              secondaryValue={
                isDisputesWidgetLoading
                  ? '...'
                  : formatCurrency(disputesWidgetSummary?.unresolvedTotalAmount ?? masterPriorities.disputes.amount)
              }
              icon={Scale}
              variant="info"
              onClick={() => {
                const params = new URLSearchParams({ status: 'New,In Progress,Escalated' });
                if (selectedUserId) params.set('assignedUser', selectedUserId);
                navigate(`/disputes/all?${params.toString()}`);
              }}
            />
          </div>

          {/* Today's Progress Summary */}
          <div className="mt-4 pt-4 border-t border-border text-center text-sm text-muted-foreground">
            <CheckCircle2 className="inline-block h-4 w-4 text-green-600 mr-2 -mt-0.5" />
            Today&apos;s progress:{' '}
            <TodaysProgressBar
              progress={todaysProgress}
              isLoading={isTodaysProgressLoading}
              isError={isTodaysProgressError}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>

        {/* Section 2: Ageing Analysis + Performance Snapshot (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Ageing Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Ageing Analysis{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isAgeingSummaryLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="space-y-2 animate-pulse">
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-5 w-24 bg-gray-200 rounded" />
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" />
                    </div>
                  ))}
                </div>
              ) : isAgeingSummaryError ? (
                <div className="text-sm text-destructive">Unable to load ageing analysis data</div>
              ) : ageingSummary ? (
                <>
                  {ageingSummary.bands.map((band) => {
                    const maxPercentage = Math.max(...ageingSummary.bands.map((b) => b.percentage));
                    const barWidth = maxPercentage > 0 ? (band.percentage / maxPercentage) * 100 : 0;
                    return (
                      <Link
                        key={band.agingBandId}
                        to={`/analytics-reporting/ageing-analysis?filter=${encodeURIComponent(band.bandLabel)}`}
                        className="block group"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">{band.bandLabel}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-900">
                                {formatCurrency(band.outstandingAmount)}
                              </span>
                              <span className="text-sm text-gray-500">({Math.round(band.percentage)}%)</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${barWidth}%`, backgroundColor: band.color || '#14b8a6' }}
                            />
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  <div className="pt-4 mt-5 border-t border-border">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Total outstanding</span>
                      <span className="text-2xl font-bold text-foreground">
                        {formatCurrency(ageingSummary.totalOutstanding)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/analytics-reporting/ageing-analysis')}
                      className="w-full justify-between text-neutral-700 hover:text-teal-600 hover:bg-teal-50"
                    >
                      <span className="text-sm font-medium">See your ageing debt</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* Performance Snapshot - Redesigned */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Snapshot{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <PerformanceSnapshotContent
                isLoading={isPerformanceSnapshotLoading}
                isError={isPerformanceSnapshotError}
                performanceSnapshot={performanceSnapshot}
                fallback={{
                  customerCount: portfolioOverview.customerCount,
                  dsoCurrent: keyMetrics.dso.current,
                  dsoTarget: keyMetrics.dso.target,
                  promiseSuccessRate: keyMetrics.promiseSuccessRate,
                  avgPaymentDays: keyMetrics.avgPaymentDays,
                  collectionRate: todaysWins.collectionRate,
                  collectionRateTarget: todaysWins.target,
                }}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
